import React from "react";
import { useSelector } from "react-redux";
import { Tag } from "@stonelog/stonelog-design-system";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import ImageGallery from "@components/ImageGallery";
import { adminStoneEntregaSelector } from "../../../../features/adminStoneEntrega/adminStoneEntregaSlice";
import {
  PATHWAY_TYPE_LABELS,
  formatEnumLabel,
  getImageFiles,
  getStatusTagType,
} from "../constants";

import * as S from "../styles";

// Marca a etapa/parada em andamento (current_pathway_id / current_stop_id)
const CurrentTag = () => <Tag text="Atual" type="info" />;

const EventRow = ({ event }) => (
  <S.EventItem>
    <span>{formatEnumLabel(event.event_type)}</span>
    {(event.previous_status || event.new_status) && (
      <small>
        {formatEnumLabel(event.previous_status)} → {formatEnumLabel(event.new_status)}
      </small>
    )}
    {event.metadata?.reason && <small>Motivo: {event.metadata.reason}</small>}
  </S.EventItem>
);

// As fotos já estão no store (quem busca é o DeliveryDrawer ao abrir)
const EvidenceRow = ({ evidence, deliveryId }) => {
  const { arquivos, arquivosStatus } = useSelector(adminStoneEntregaSelector);

  const imageFiles = getImageFiles(evidence);
  const imagens = imageFiles.map((file) => arquivos[file.file_hash]).filter(Boolean);
  const indisponiveis = imageFiles.length - imagens.length;

  return (
    <S.EventItem>
      <span>Evidência de {formatEnumLabel(evidence.evidence_type)}</span>
      {(evidence.receiver_name || evidence.full_name) && (
        <small>Recebedor: {evidence.full_name || evidence.receiver_name}</small>
      )}
      {imagens.length > 0 && (
        <ImageGallery
          images={imagens}
          fileName={`delivery-${deliveryId}-evidencia-${evidence.id}`}
        />
      )}
      {indisponiveis > 0 && (
        <S.AttachmentsHint>
          {arquivosStatus === "pending"
            ? "Carregando anexos..."
            : `${indisponiveis} anexo(s) indisponível(is)`}
        </S.AttachmentsHint>
      )}
    </S.EventItem>
  );
};

// Parada (stop): tipo, status e, dentro dela, os eventos e as evidências
const StopItem = ({ delivery, stop }) => (
  <S.StopCard>
    <S.StopHeader>
      <p>{formatEnumLabel(stop.stop_type)}</p>
      <S.StopTags>
        <Tag text={formatEnumLabel(stop.status)} type={getStatusTagType(stop.status)} />
        {stop.id === delivery.current_stop_id && <CurrentTag />}
      </S.StopTags>
    </S.StopHeader>
    {(stop.events || []).map((event) => (
      <EventRow key={event.id} event={event} />
    ))}
    {(stop.evidences || []).map((evidence) => (
      <EvidenceRow key={evidence.id} evidence={evidence} deliveryId={delivery.id} />
    ))}
  </S.StopCard>
);

// Etapa (pathway): cabeçalho que abre/fecha e, aberta, o motivo de ineficiência e as paradas
const PathwayItem = ({ delivery, pathway, isOpen, onToggle }) => {
  const stops = [...(pathway.stops || [])].sort((a, b) => a.stop_index - b.stop_index);

  return (
    <S.PathwayCard>
      <S.PathwayHeader $isOpen={isOpen} onClick={onToggle}>
        <S.PathwayTitle>
          <p>{PATHWAY_TYPE_LABELS[pathway.pathway_type] || formatEnumLabel(pathway.pathway_type)}</p>
          <Tag text={formatEnumLabel(pathway.status)} type={getStatusTagType(pathway.status)} />
          {pathway.id === delivery.current_pathway_id && <CurrentTag />}
        </S.PathwayTitle>
        <GingaIcon name={isOpen ? "chevron-up" : "chevron-down"} color="#6A7181" size={24} />
      </S.PathwayHeader>

      {isOpen && (
        <>
          {pathway.inefficiency_reason && (
            <S.BodyTopContent>
              <p>Motivo de ineficiência</p>
              <span>{`${formatEnumLabel(pathway.inefficiency_flow)}: ${pathway.inefficiency_reason}`}</span>
            </S.BodyTopContent>
          )}
          {stops.map((stop) => (
            <StopItem key={stop.id} delivery={delivery} stop={stop} />
          ))}
        </>
      )}
    </S.PathwayCard>
  );
};

// Linha do tempo: etapas por pathway_index. A etapa em andamento abre sozinha; o que o
// usuário abrir/fechar fica em `openPathways` (estado do drawer).
const DeliveryTimeline = ({ delivery, openPathways, onTogglePathway }) => {
  const pathways = [...(delivery.pathways || [])].sort(
    (a, b) => a.pathway_index - b.pathway_index
  );

  if (pathways.length === 0) return <p>Nenhuma etapa registrada.</p>;

  return pathways.map((pathway) => {
    const isOpen =
      pathway.id in openPathways
        ? openPathways[pathway.id]
        : pathway.id === delivery.current_pathway_id;

    return (
      <PathwayItem
        key={pathway.id}
        delivery={delivery}
        pathway={pathway}
        isOpen={isOpen}
        onToggle={() => onTogglePathway(pathway.id, !isOpen)}
      />
    );
  });
};

export default DeliveryTimeline;
